import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiFile, FiUploadCloud, FiDownload, FiTrash2, FiPaperclip } from 'react-icons/fi';
import { useToast } from '../components/Toast';

const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

function DocumentsView({ userId }) {
  const toast = useToast();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [bookingId, setBookingId] = useState('');
  const [docType, setDocType] = useState('case_file');

  useEffect(() => {
    fetchDocuments();
  }, [userId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/documents/?user_id=${userId}`);
      if (response.data.success) {
        setDocuments(response.data.data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to load documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !bookingId) {
      toast.warning('Please select a file and booking');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('booking_id', bookingId);
      formData.append('document_type', docType);
      formData.append('document_name', selectedFile.name);
      formData.append('user_id', userId);

      const response = await axios.post(`${API_BASE}/documents/?user_id=${userId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setSelectedFile(null);
        setBookingId('');
        setDocType('case_file');
        fetchDocuments();
        toast.success('Document uploaded successfully!');
      }
    } catch (err) {
      toast.error('Failed to upload document: ' + err.message);
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await axios.delete(`${API_BASE}/documents/?id=${docId}&user_id=${userId}`);
      if (response.data.success) {
        setDocuments(documents.filter(d => d.id !== docId));
      }
    } catch (err) {
      toast.error('Failed to delete document');
      console.error(err);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading && documents.length === 0) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      <span className="ml-3 text-gray-500">Loading documents...</span>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FiFile className="w-5 h-5 text-accent" />
          Documents
        </h2>
        <p className="text-sm text-gray-500 mt-1">Access your case files and documents</p>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiUploadCloud className="w-5 h-5 text-primary" />
          Upload Document
        </h3>
        <form onSubmit={handleFileUpload} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[120px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Booking ID</label>
            <input
              type="number"
              placeholder="Booking ID"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Document Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-white"
            >
              <option value="case_file">Case File</option>
              <option value="agreement">Agreement</option>
              <option value="judgment">Judgment</option>
              <option value="evidence">Evidence</option>
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">File</label>
            <input
              type="file"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              accept=".pdf,.doc,.docx,.jpg,.png,.txt"
              required
              className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/5 file:text-primary hover:file:bg-primary/10"
            />
          </div>
          <button
            type="submit"
            disabled={uploading}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-light disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors shadow-md"
          >
            <FiUploadCloud className="w-4 h-4" />
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </form>
      </div>

      {error && <div className="text-center py-4 text-red-500 text-sm">{error}</div>}

      {/* Documents List */}
      <div className="space-y-3">
        {documents.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-card p-12 text-center">
            <FiFile className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No documents yet. Upload your first document.</p>
          </div>
        ) : (
          documents.map((doc, idx) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-white rounded-2xl shadow-card p-4 flex items-center gap-4 hover:shadow-card-hover transition-shadow duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center flex-shrink-0">
                <FiPaperclip className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900 text-sm truncate">{doc.document_name}</h4>
                <p className="text-xs text-primary font-medium">{doc.document_type.replace('_', ' ').toUpperCase()}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatFileSize(doc.file_size)} • Uploaded by {doc.uploaded_by_name} •{' '}
                  {new Date(doc.created_at).toLocaleDateString('en-IN')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={`${process.env.REACT_APP_API_URL}/${doc.file_path}`}
                  download
                  className="flex items-center gap-1.5 px-3 py-2 bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-xl text-xs font-medium transition-colors"
                >
                  <FiDownload className="w-3.5 h-3.5" /> Download
                </a>
                <button
                  onClick={() => handleDeleteDocument(doc.id)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl text-xs font-medium transition-colors"
                >
                  <FiTrash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </motion.div>
  );
}

export default DocumentsView;
